import { useLocation } from "@tanstack/react-router";
import type { MRT_ColumnFiltersState, MRT_SortingState } from "mantine-react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTab } from "src/contexts/tabContext";
import { TourProvider, useTour } from "src/contexts/tourContext";
import { ColumnType } from "src/db";
import { useColumns } from "src/hooks/useDatasources";
import { documentViewerTourSteps } from "./documentViewerTourSteps";
import { GuidedTour } from "./GuidedTour";
import {
    createDateColumn,
    createStringColumn,
    getMetadataValue,
    normalizeStringArray,
} from "./MetadataDocuments/columns/columnHelpers";
import { createLinkColumn } from "./MetadataDocuments/columns/linkColumnHelper";
import { useMetadataDocumentsQuery } from "./MetadataDocuments/hooks/useMetadataDocumentsQuery";
import { MetadataDocumentsTable } from "./MetadataDocuments/MetadataDocumentsTable";
import { flattenMetadataHits } from "./MetadataDocuments/utils";
import { PdfFocusProvider } from "./PDFViewer/context";

interface SearchHitsTotal {
    value: number;
    relation: string;
}

interface SearchResponsePage {
    hits: {
        total: SearchHitsTotal;
        hits: unknown[];
    };
}

interface QueryData {
    pages?: SearchResponsePage[];
}

const MetadataDocuments = ({
    index,
    onFilteredCountChange,
}: {
    index: string;
    onFilteredCountChange?: (count: number) => void;
}) => {
    const [columns, setColumns] = useState([]);
    const location = useLocation();
    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState<string>();
    const columnsQuery = useColumns(index);

    // Track previous index to reset filters when it changes
    const prevIndexRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (prevIndexRef.current !== undefined && prevIndexRef.current !== index) {
            // Reset all filters when the index prop changes
            setColumnFilters([]);
            setGlobalFilter(undefined);
            setSorting([]);
        }
        prevIndexRef.current = index;
    }, [index]);

    useEffect(() => {
        if (!columnsQuery.data) return;
        const namespace = columnsQuery.data.namespace;
        const columns = columnsQuery.data.index_description.map((index) => {
            const header = index.header_name;
            const field = index.name;
            const type = index.type;
            switch (type) {
                case ColumnType.DATE:
                    return createDateColumn({
                        namespace,
                        field,
                        header,
                    });

                case ColumnType.URL:
                    return createLinkColumn({
                        namespace,
                        field,
                        header,
                    });

                case ColumnType.BOOLEAN:
                    return createStringColumn({
                        namespace,
                        field,
                        header,
                        accessor: (row) => (getMetadataValue(row, field) ? "Yes" : "No"),
                    });

                default:
                    return createStringColumn({
                        namespace,
                        field,
                        header,
                        accessor: (row) =>
                            normalizeStringArray(
                                getMetadataValue(row, field) as string[] | undefined,
                            ),
                    });
            }
        });
        setColumns(columns);
    }, [columnsQuery.data]);

    const { data, fetchNextPage, isFetching, isLoading } = useMetadataDocumentsQuery({
        index,
        sorting,
        columnFilters,
        globalFilter,
    });

    const flatData = useMemo(() => flattenMetadataHits(data, index), [data, index]);

    const filteredCount = useMemo(() => {
        const typedData = data as QueryData;
        return typedData?.pages?.[0]?.hits?.total?.value ?? 0;
    }, [data]);

    const hasActiveFilters = useMemo(() => {
        return columnFilters.length > 0 || globalFilter !== undefined;
    }, [columnFilters, globalFilter]);

    useEffect(() => {
        if (onFilteredCountChange) {
            onFilteredCountChange(hasActiveFilters ? filteredCount : undefined);
        }
    }, [filteredCount, onFilteredCountChange, hasActiveFilters]);

    const { setSelectedTab } = useTab();

    useEffect(() => {
        setSelectedTab("field_service_reports");
    }, [setSelectedTab]);

    function TourConsumer() {
        const { isTourOpen, completeTour } = useTour();
        return (
            <GuidedTour
                steps={documentViewerTourSteps}
                onComplete={completeTour}
                onClose={completeTour}
                isOpen={isTourOpen}
                pathCheck={() => location.pathname.includes("/documents")}
            />
        );
    }
    return (
        <TourProvider tourId="docs-tour">
            <TourConsumer />
            <PdfFocusProvider>
                <MetadataDocumentsTable
                    index={index}
                    columns={columns}
                    data={flatData}
                    queryData={data}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    sorting={sorting}
                    setSorting={setSorting}
                    columnFilters={columnFilters}
                    setColumnFilters={setColumnFilters}
                    globalFilter={globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    fetchNextPage={fetchNextPage}
                />
            </PdfFocusProvider>
        </TourProvider>
    );
};

export default MetadataDocuments;
